import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, UserPlus, UserCheck, UserX, MapPin, Send, Radio,
  MessageCircle, Loader2, ShieldCheck, Plane, Search,
} from "lucide-react";

/**
 * FlyingBuddies — friends/followers module shown on the Cockpit profile page.
 * Shows accepted buddies with online status + inbox shortcut, incoming and
 * outgoing pending requests, and a link to browse the pilot search to add
 * more people.
 */
export default function FlyingBuddies() {
  const [data, setData] = useState({ buddies: [], pending_in: [], pending_out: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/buddies");
      setData(r.data || { buddies: [], pending_in: [], pending_out: [] });
    } catch (e) {
      // fall through; empty state renders fine
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    setBusy((b) => ({ ...b, [id + verb]: true }));
    try {
      if (verb === "accept") await api.post(`/buddies/accept/${id}`);
      else if (verb === "decline") await api.post(`/buddies/decline/${id}`);
      else if (verb === "remove") await api.delete(`/buddies/${id}`);
      toast.success(verb === "accept" ? "Flying Buddy added" : verb === "decline" ? "Request declined" : "Flying Buddy removed");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    } finally {
      setBusy((b) => ({ ...b, [id + verb]: false }));
    }
  };

  return (
    <div className="mt-4" data-testid="flying-buddies">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// FLYING BUDDIES</div>
          <div className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mt-0.5">Your network in the air</div>
          <div className="text-sm text-white/55 mt-1">See who's online, where they're based and message them in one tap.</div>
        </div>
        <Link
          to="/search"
          data-testid="buddies-find-more"
          className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-cyan/40 text-cyan hover:bg-cyan/[0.10] inline-flex items-center gap-1.5"
        >
          <Search className="h-3 w-3" /> FIND PILOTS
        </Link>
      </div>

      {loading ? (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-cyan" />
        </div>
      ) : (
        <>
          {/* Incoming requests */}
          {data.pending_in.length > 0 && (
            <div className="mb-4 rounded-lg border border-cyan/30 bg-cyan/[0.05] p-3" data-testid="buddies-pending-in">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan mb-2 flex items-center gap-1.5">
                <UserPlus className="h-3 w-3" /> INCOMING FLYING BUDDY REQUESTS · {data.pending_in.length}
              </div>
              <div className="space-y-2">
                {data.pending_in.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 flex-wrap">
                    <Avatar u={u} />
                    <div className="min-w-0 flex-1">
                      <Link to={`/crew/${u.id}`} className="text-sm text-white hover:text-cyan truncate">
                        {u.nickname || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "PJP Pilot"}
                      </Link>
                      <div className="mono text-[9px] tracking-widest uppercase text-white/45">{u.role_title || u.role || "MEMBER"}</div>
                    </div>
                    <button
                      onClick={() => act(u.id, "accept")}
                      disabled={busy[u.id+"accept"]}
                      data-testid={`buddy-accept-${u.id}`}
                      className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded bg-avail text-obsidian font-bold hover:brightness-110 inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      {busy[u.id+"accept"] ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />} ACCEPT
                    </button>
                    <button
                      onClick={() => act(u.id, "decline")}
                      disabled={busy[u.id+"decline"]}
                      data-testid={`buddy-decline-${u.id}`}
                      className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-white/20 text-white/70 hover:bg-white/5 inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      <UserX className="h-3 w-3" /> DECLINE
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Accepted buddies grid */}
          {data.buddies.length === 0 && data.pending_in.length === 0 && data.pending_out.length === 0 ? (
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6 text-center" data-testid="buddies-empty">
              <Users className="h-8 w-8 text-cyan/60 mx-auto mb-2" />
              <div className="text-white/80 text-sm">No Flying Buddies yet.</div>
              <div className="text-white/50 text-xs mt-1">Add pilots to see who's online and where they're based — then DM them straight from here.</div>
              <Link
                to="/search"
                data-testid="buddies-empty-cta"
                className="mono text-[10px] tracking-widest uppercase px-4 py-2 rounded bg-cyan text-obsidian font-bold hover:brightness-110 inline-flex items-center gap-1.5 mt-4"
              >
                <Search className="h-3 w-3" /> FIND PILOTS TO ADD
              </Link>
            </div>
          ) : data.buddies.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="buddies-grid">
              {data.buddies.map((u) => (
                <BuddyCard key={u.id} u={u} onRemove={() => act(u.id, "remove")} busy={busy[u.id+"remove"]} />
              ))}
            </div>
          ) : null}

          {/* Outgoing pending */}
          {data.pending_out.length > 0 && (
            <div className="mt-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3" data-testid="buddies-pending-out">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-white/60 mb-2">// PENDING FLYING BUDDIES · YOU SENT · {data.pending_out.length}</div>
              <div className="flex flex-wrap gap-2">
                {data.pending_out.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 rounded border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
                    <Avatar u={u} sm />
                    <Link to={`/crew/${u.id}`} className="text-xs text-white/80 hover:text-cyan truncate">
                      {u.nickname || `${u.first_name || ""} ${u.last_name || ""}`.trim()}
                    </Link>
                    <span className="mono text-[9px] tracking-widest uppercase text-white/40">PENDING</span>
                    <button
                      onClick={() => act(u.id, "remove")}
                      disabled={busy[u.id+"remove"]}
                      data-testid={`buddy-cancel-${u.id}`}
                      className="mono text-[9px] tracking-widest uppercase text-white/50 hover:text-red-400"
                      title="Cancel request"
                    >
                      CANCEL
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BuddyCard({ u, onRemove, busy }) {
  const name = u.nickname || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "PJP Pilot";
  const homeBase = u.location_icao || u.home_base_icao;
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:border-cyan/30 transition-colors" data-testid={`buddy-card-${u.id}`}>
      <div className="flex items-start gap-3">
        <div className="relative">
          <Avatar u={u} />
          {u.online && (
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-avail border-2 border-obsidian animate-pulse"
              title="Online now"
              data-testid={`buddy-online-${u.id}`}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <Link to={`/crew/${u.id}`} data-testid={`buddy-name-${u.id}`} className="text-sm font-medium text-white hover:text-cyan truncate block">{name}</Link>
          <div className="mono text-[9px] tracking-widest uppercase text-white/45 flex items-center gap-1.5 flex-wrap mt-0.5">
            <span>{u.role_title || u.role || "MEMBER"}</span>
            {u.faa_verified && <span className="inline-flex items-center gap-0.5 text-avail"><ShieldCheck className="h-2.5 w-2.5" /> FAA</span>}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-white/60">
        {homeBase && (
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-cyan/70" /> {homeBase}</span>
        )}
        {u.share_live_location && (
          <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-cyan/15 text-cyan border border-cyan/40 inline-flex items-center gap-1"><Radio className="h-2.5 w-2.5" /> LIVE</span>
        )}
        {u.online ? (
          <span className="mono text-[9px] tracking-widest uppercase text-avail">ONLINE</span>
        ) : u.last_active_at ? (
          <span className="mono text-[9px] tracking-widest uppercase text-white/40">{relTime(u.last_active_at)}</span>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Link
          to={`/inbox?user=${u.id}`}
          data-testid={`buddy-dm-${u.id}`}
          className="flex-1 mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 inline-flex items-center justify-center gap-1.5"
        >
          <MessageCircle className="h-3 w-3" /> MESSAGE
        </Link>
        <button
          onClick={onRemove}
          disabled={busy}
          data-testid={`buddy-remove-${u.id}`}
          title="Remove Flying Buddy"
          className="mono text-[9px] tracking-widest uppercase px-2.5 py-1.5 rounded border border-white/15 text-white/50 hover:border-red-400/60 hover:text-red-400 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserX className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}

function Avatar({ u, sm = false }) {
  const initials = ((u.first_name || "?")[0] + (u.last_name || "")[0]).toUpperCase();
  const size = sm ? "h-6 w-6 text-[9px]" : "h-10 w-10 text-[11px]";
  return (
    <div className={`${size} rounded-lg overflow-hidden border border-cyan/30 bg-cyan/5 flex items-center justify-center flex-shrink-0`}>
      {u.avatar_url ? (
        <img src={u.avatar_url} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
      ) : (
        <span className="mono text-cyan tracking-widest">{initials}</span>
      )}
    </div>
  );
}

function relTime(iso) {
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}
